import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

export interface WizardShellProps {
  currentStep: number;
  totalSteps: number;
  stepLabel: string;
  isOptional?: boolean;
  onBack: () => void;
  onNext: () => void;
  onSkip?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  nextLoading?: boolean;
  children: React.ReactNode;
  stepLabels?: readonly string[];
}

const DEFAULT_STEP_LABELS = [
  "Basics",
  "Airbnb",
  "Rooms",
  "Checklist",
  "Inventory",
  "Calendar",
  "Team",
  "Done",
];

export function WizardShell({
  currentStep,
  totalSteps,
  stepLabel,
  isOptional = false,
  onBack,
  onNext,
  onSkip,
  nextLabel = "Continue",
  nextDisabled = false,
  nextLoading = false,
  children,
  stepLabels = DEFAULT_STEP_LABELS,
}: WizardShellProps) {
  const direction = 1;

  return (
    <div className="flex flex-col flex-1 min-h-0 h-full">
      <header className="border-b bg-card/80 shrink-0 px-4 py-4">
        <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-1 scrollbar-none">
          {stepLabels.slice(0, totalSteps).map((label, i) => {
            const stepNum = i + 1;
            const isComplete = stepNum < currentStep;
            const isActive = stepNum === currentStep;
            return (
              <div key={label} className="flex items-center gap-1 sm:gap-2 shrink-0">
                <div
                  className={cn(
                    "flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium transition-colors",
                    isComplete && "text-primary",
                    isActive && "bg-primary/10 text-primary",
                    !isComplete && !isActive && "text-muted-foreground"
                  )}
                >
                  <span
                    className={cn(
                      "flex h-6 w-6 items-center justify-center rounded-full border text-[10px] sm:text-xs",
                      isComplete && "border-primary bg-primary text-primary-foreground",
                      isActive && "border-primary",
                      !isComplete && !isActive && "border-muted-foreground/30"
                    )}
                  >
                    {isComplete ? <Check className="h-3.5 w-3.5" /> : stepNum}
                  </span>
                  <span className="hidden sm:inline max-w-[4.5rem] truncate">{label}</span>
                </div>
                {i < totalSteps - 1 && (
                  <div
                    className={cn(
                      "h-px w-3 sm:w-6",
                      stepNum < currentStep ? "bg-primary" : "bg-border"
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Step {currentStep} of {totalSteps}:{" "}
          <span className="text-foreground font-medium">{stepLabel}</span>
        </p>
      </header>

      <main className="flex-1 overflow-y-auto min-h-0 px-6 py-6">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.2 }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      {currentStep < totalSteps && (
        <footer className="border-t bg-card shrink-0 px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              disabled={nextLoading}
              className="shrink-0"
            >
              Back
            </Button>
            <div className="flex-1 flex justify-center">
              {isOptional && onSkip && (
                <Button type="button" variant="ghost" onClick={onSkip} disabled={nextLoading}>
                  Skip for now
                </Button>
              )}
            </div>
            <Button
              type="button"
              onClick={onNext}
              disabled={nextDisabled || nextLoading}
              className="shrink-0 min-w-[7rem]"
            >
              {nextLoading ? "Saving…" : nextLabel}
            </Button>
          </div>
        </footer>
      )}
    </div>
  );
}
